import React, { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { HiOutlineX } from "react-icons/hi";
import Button from "../../../../components/shared/Button/Button";
import Divider from "../../../../components/shared/Divider/Divider";
import Input from "../../../../components/shared/Input/Input";
import Select from "../../../../components/shared/Select/Select";
import { useAdminSearchStore } from "../../store/adminSearchStore";

export const STATUS_OPTIONS = [
  { value: "pending", text: "Pending" },
  { value: "approved", text: "Approved" },
  { value: "rejected", text: "Rejected" },
];

const formSchema = z.object({
  senderName: z.string().optional(),
  invitee: z.string().optional(),
  inviteeName: z.string().optional(),
  status: z.string().optional(),
});

export function InvitesFiltersPanel({ isOpen, onClose, onSubmit }) {
  const { invitesState, setInvitesFilters } = useAdminSearchStore();
  const { filters } = invitesState;

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isDirty },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      senderName: "",
      invitee: "",
      inviteeName: "",
      status: "",
    },
    mode: "onChange",
  });

  const watchedValues = watch();

  useEffect(() => {
    if (isOpen) {
      reset({
        senderName: filters.senderName || "",
        invitee: filters.invitee || "",
        inviteeName: filters.inviteeName || "",
        status: filters.status || "",
      });
    }
  }, [filters, isOpen, reset]);

  const hasActiveFilters = useMemo(() => {
    return Object.values(watchedValues).some((v) => (v || "").trim() !== "");
  }, [watchedValues]);

  const handleClear = useCallback(() => {
    reset({
      senderName: "",
      invitee: "",
      inviteeName: "",
      status: "",
    });
    setInvitesFilters({
      senderName: null,
      invitee: null,
      inviteeName: null,
      status: null,
    });
    onSubmit?.();
  }, [reset, setInvitesFilters, onSubmit]);

  const handleApply = useCallback(
    (data) => {
      setInvitesFilters({
        senderName: data.senderName?.trim() || null,
        invitee: data.invitee?.trim() || null,
        inviteeName: data.inviteeName?.trim() || null,
        status: data.status?.trim() || null,
      });
      onSubmit?.();
    },
    [setInvitesFilters, onSubmit]
  );

  if (!isOpen) return null;

  return (
    <div
      aria-label="Invite Filters"
      aria-modal="true"
      className="font-manrope fixed right-0 top-0 z-[2000] flex h-screen w-full max-w-96 flex-col border-l border-gray200 bg-white p-6"
      role="dialog"
    >
      <div className="relative flex flex-col gap-1">
        <div className="text-2xl font-semibold">Filters</div>
        <div className="text-base text-gray600">Filter invites by criteria</div>
        <button
          aria-label="Close"
          className="absolute right-0 top-0 w-fit rounded-sm p-2"
          onClick={onClose}
        >
          <HiOutlineX className="size-6 text-gray500" />
        </button>
      </div>

      <div className="mt-6 flex flex-1 flex-col gap-6 overflow-y-auto scrollbar-hide">
        <Input
          control={control}
          name="senderName"
          label="Sender Name"
          placeholder="Sender name"
          size="md"
        />

        <Input
          control={control}
          name="invitee"
          label="Invitee (email)"
          placeholder="Invitee email"
          size="md"
        />

        <Input
          control={control}
          name="inviteeName"
          label="Invitee Name"
          placeholder="Invitee name"
          size="md"
        />

        <div className="flex flex-col gap-2">
          <label className="text-base text-gray700">Status</label>
          <Select
            options={STATUS_OPTIONS}
            value={watchedValues.status || ""}
            onChange={(val) => setValue("status", val || "")}
            placeholder="Select status"
            buttonProps={{ size: "md" }}
          />
        </div>
      </div>

      <div className="mt-auto">
        <Divider type="horizontal" color="gray200" />

        <div className="flex items-center justify-between pt-4">
          <button
            className={`text-lg font-semibold ${
              !hasActiveFilters ? "text-gray300" : "text-primary700"
            }`}
            onClick={handleClear}
          >
            Clear all
          </button>

          <div className="flex gap-2">
            <Button color="gray100" onClick={onClose} size="md">
              Cancel
            </Button>

            <Button
              onClick={handleSubmit(handleApply)}
              size="md"
              isDisabled={!hasActiveFilters && !isDirty}
            >
              Apply
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
